import { acceptHMRUpdate, defineStore } from 'pinia'
import api from '@api'
import type {
  BookingLinkBookingCreate,
  BookingLinkBookingUpdate,
  ByEmailBookingsAddressesListParams,
  ByEmailBookingsListParams,
  ByHashBookingLinksAvailableSlotsRetrieveParams,
  ByHashContactsAuctionsBookingSlotsAvailableSlotsRetrieveParams,
  TimeSlotBookingCarrierCreate,
  TimeSlotBookingCarrierUpdate,
} from '@apiTypes'
import type { CarrierAuctionHashes } from '@/utils/carrier.utils'

// The hash booking pages must not send a stale portal OTP token along.
const byHashRequestParams = { skipAuthToken: true }

type CarrierAvailableSlotsQuery = Omit<
  ByHashContactsAuctionsBookingSlotsAvailableSlotsRetrieveParams,
  keyof CarrierAuctionHashes
>

type BookingLinkAvailableSlotsQuery = Omit<ByHashBookingLinksAvailableSlotsRetrieveParams, 'hash'>

export const useCarrierBookingsStore = defineStore('carrierBookings', {
  actions: {
    fetchCarrierBookings(query: ByEmailBookingsListParams) {
      return api.byEmail.byEmailBookingsList(query)
    },

    /** Portal read used only to redirect old portal booking URLs to the hash pages. */
    fetchCarrierBooking(hash: string) {
      return api.byEmail.byEmailBookingsRetrieve({ hash })
    },
    fetchCarrierBookingAddresses(query: ByEmailBookingsAddressesListParams) {
      return api.byEmail.byEmailBookingsAddressesList(query)
    },
    fetchCarrierBookingShippers() {
      return api.byEmail.byEmailBookingsShippersRetrieve()
    },
    fetchCarrierBookingPrefillByHash(hashes: CarrierAuctionHashes, waypoint: number) {
      return api.byHash.byHashContactsAuctionsBookingsPrefillRetrieve({ ...hashes, waypoint }, byHashRequestParams)
    },
    fetchCarrierAvailableSlotsByHash(hashes: CarrierAuctionHashes, query: CarrierAvailableSlotsQuery) {
      return api.byHash.byHashContactsAuctionsBookingSlotsAvailableSlotsRetrieve(
        { ...hashes, ...query },
        byHashRequestParams,
      )
    },
    createCarrierBookingByHash(hashes: CarrierAuctionHashes, data: TimeSlotBookingCarrierCreate) {
      return api.byHash.byHashContactsAuctionsBookingsCreate(hashes, data, byHashRequestParams)
    },
    fetchCarrierBookingByHash(hashes: CarrierAuctionHashes, hash: string) {
      return api.byHash.byHashContactsAuctionsBookingsRetrieve({ ...hashes, hash }, byHashRequestParams)
    },
    updateCarrierBookingByHash(hashes: CarrierAuctionHashes, hash: string, data: TimeSlotBookingCarrierUpdate) {
      return api.byHash.byHashContactsAuctionsBookingsUpdate({ ...hashes, hash }, data, byHashRequestParams)
    },
    cancelCarrierBookingByHash(hashes: CarrierAuctionHashes, hash: string) {
      return api.byHash.byHashContactsAuctionsBookingsCancelCreate({ ...hashes, hash }, byHashRequestParams)
    },
    fetchBookingLinkByHash(hash: string) {
      return api.byHash.byHashBookingLinksRetrieve({ hash }, byHashRequestParams)
    },
    fetchBookingLinkAvailableSlots(hash: string, query: BookingLinkAvailableSlotsQuery) {
      return api.byHash.byHashBookingLinksAvailableSlotsRetrieve({ hash, ...query }, byHashRequestParams)
    },
    createBookingLinkBooking(hash: string, data: BookingLinkBookingCreate) {
      return api.byHash.byHashBookingLinksBookingCreate({ hash }, data, byHashRequestParams)
    },
    fetchBookingLinkBooking(hash: string) {
      return api.byHash.byHashBookingLinksBookingRetrieve({ hash }, byHashRequestParams)
    },
    updateBookingLinkBooking(hash: string, data: BookingLinkBookingUpdate) {
      return api.byHash.byHashBookingLinksBookingUpdate({ hash }, data, byHashRequestParams)
    },
  },
})

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useCarrierBookingsStore, import.meta.hot))
